import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { BaseComponent, environment } from '@eview/core';
import { AuthHelpers } from '@eview/core/auth';
import { Permission } from '@eview/core/auth/permission';
import { FeatureHelpers } from '@eview/core/domain/post/feature.helpers';
import {
  PostStatus,
  ListPostOptions,
  Post,
  PostType,
  CategoriesEnum,
  LegendTypeEnum
} from '@eview/core/domain/post/post';
import { PostService } from '@eview/core/domain/post/post.service';
import {
  EMapActions,
  SetMapMarkers,
  UserClickedMap,
  UserClickedMarker
} from '@eview/core/store/actions/map.actions';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import {
  Subscription,
  BehaviorSubject,
  interval,
  Observable,
  combineLatest,
  of,
  forkJoin
} from 'rxjs';
import {
  filter,
  switchMap,
  take,
  tap,
  map,
  distinctUntilChanged,
  catchError,
  debounceTime,
  shareReplay
} from 'rxjs/operators';
import {
  ECustomActions,
  ShowPostDetail,
  DeletedPost
} from '../../shared/custom.store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TagSelectorSetById } from '../../shared/post-editor/tag-selector/tag-selector.component';
import { ListForms } from '@eview/core/store/actions/forms.actions';
import { ListFormAttributes } from '@eview/core/store//actions/form-attributes.actions';
import { selectForms } from '@eview/core/store/selectors/forms.selector';
import { Form, sortFormsDefaultFirst } from '@eview/core/domain/form/form';
import { Store } from '@ngrx/store';
import { FilterService } from '../../shared/filters/filters.service';
import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { selectPosts } from '@eview/core/store/selectors/posts.selector';
import { ActivatedRoute } from '@angular/router';
import { from } from 'rxjs';
import { GetLinkedPostsData, GetPostsData } from '@eview/core/store/actions/posts.actions';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { User } from '@eview/core/models/user';
import { selectTags } from '@eview/core/store/selectors/tags.selector';
import { selectCategories } from '@eview/core/store/selectors/tags.selector';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';
import { ListCategoriesSuccess } from '@eview/core/store/actions/categories.actions';
import { TagHelpers } from '@eview/core/domain/tags/tag.helpers';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { selectFormAttributesLoadedFormId } from '@eview/core/store/selectors/form-attributes.selector';
import { selectUserPermissions } from '@eview/core/store/selectors/user-permissions.selector';
import { UserPermissions } from '@eview/core/models/user-permissions';
import { CatalogFacade } from '@eview/core/store/facades/catalog.facade';

const PAGE_SIZE = 20;
@Component({
  selector: 'eview-map-page',
  templateUrl: 'map-page.component.html',
  styleUrls: ['map-page.component.scss']
})
export class MapPageComponent extends BaseComponent
  implements OnInit, OnDestroy {

  @ViewChild('mapComponent') mapComponent: any;

  tenant$: Observable<any>;
  posts$: Observable<any[]>;
  viewPost$: Observable<boolean>;
  user$: Observable<User>;
  viewOnlyMode$: Observable<any>;
  viewOnlyModeLegend$: Observable<any>;
  navData$: Observable<any>;
  /** Ids de formatos con ≥1 informe público (para rail y filtros anónimos). */
  formsWithPublishedPosts$: Observable<number[] | undefined>;
  publishedFormFilterIds: number[] | null = null;
  viewData$: Observable<any>;
  tags$: Observable<any[]>;
  categories$: Observable<any[]>;
  categoriesData$: Observable<any>;
  statusData$: Observable<any[]>;

  aboutUsImageUrl$: Observable<string>;

  mainPost: Post;

  tagName: string;

  selectedPost: Post;

  isLoadAllReport: boolean = false;

  applyDisabled: boolean = true;

  isErrorReport: boolean = false;

  loadAllParentReport: boolean = false;

  userCanSubmitPosts: boolean = false;

  userCanFilterPosts: boolean = false;

  showPostDetail: boolean = false;

  resetFilters = new BehaviorSubject(false);

  private subs: Subscription;

  AuthHelpers = AuthHelpers;

  Permission = Permission;

  PostHelpers = PostHelpers;

  /** Legend statuses for the active map form (or union if form unknown). */
  get mapStatusLegendData() {
    const form = this.formList?.find(f => f.id === this.selectedMapFormId);
    if (!form) {
      return PostHelpers.GetAllStatusMapping('all');
    }
    return PostHelpers.GetAllStatusMapping(
      PostHelpers.GetStatusSchemeFromForm(form)
    );
  }

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private postService: PostService,
    private modalService: NgbModal,
    private filterService: FilterService,
    private route: ActivatedRoute,
    private catalog: CatalogFacade
  ) {
    super();
    this.subs = new Subscription();
    this.tenant$ = this.store.select(selectTenant);
    this.posts$ = this.store.select(selectPosts).pipe(
      map(data => (data ? (data.results.length ? data.results : []) : []))
    );
    this.viewPost$ = this.store.select(selectUser).pipe(map(users => (users ? true : true)));
    this.user$ = this.store.select(selectUser);
    this.viewOnlyMode$ = this.store.select(selectUser).pipe(map(users => (users ? true : false)));
    this.viewOnlyModeLegend$ = this.store.select(selectUser).pipe(map(users => (users ? true : true)));
    this.navData$ = combineLatest([
      this.tenant$,
      this.store.select(selectUserPermissions),
      this.store.select(selectUser)
    ]).pipe(
      switchMap(([tenant, userPermissions, user]) => {
        // Anonymous: public map navigation (no form-grant restrictions).
        if (user == null) {
          return of(
            tenant.mapNavigation.navData.filter(
              (v: { route?: string }) => v.route !== '/dashboard/map/mace'
            )
          );
        }

        if (!userPermissions) {
          return of(tenant.mapNavigation.navData.filter(v => v.route !== '/dashboard/map/mace'));
        }

        const hasConflictividadPermission = AuthHelpers.User.canFormSync(
          userPermissions,
          1,
          'view'
        );
        const hasElectionPermission = AuthHelpers.User.canFormSync(
          userPermissions,
          2,
          'view'
        );

        if (hasConflictividadPermission && hasElectionPermission) {
          return of(tenant.mapNavigation.navData);
        } else if (hasConflictividadPermission) {
          return of(tenant.mapNavigation.navData.filter(v => v.route === '/dashboard/map'));
        } else {
          return of(tenant.mapNavigation.navData.filter(v => v.route !== '/dashboard/map'));
        }
      })
    );
    /** Solo anónimo: ids de formatos con informes públicos. Logueado: `undefined` (sin filtrar). */
    this.formsWithPublishedPosts$ = combineLatest([
      this.store.select(selectUser),
      this.store.select(selectForms).pipe(filter(f => f !== null))
    ]).pipe(
      debounceTime(150),
      switchMap(([user, formsState]) => {
        if (!formsState?.results?.length || user != null) {
          return of(undefined);
        }
        return this.fetchFormIdsWithPublishedPosts(formsState).pipe(
          catchError(() => of([] as number[]))
        );
      }),
      shareReplay(1)
    );

    this.viewData$ = combineLatest([
      this.tenant$,
      this.store.select(selectForms),
      this.store.select(selectUser),
      this.formsWithPublishedPosts$,
      this.store.select(selectUserPermissions)
    ]).pipe(
      map(([tenant, formsState, user, publishedFormIds, userPermissions]) =>
        MapPageComponent.buildMapViewDataTabs(
          tenant,
          formsState,
          user,
          this.router.url,
          publishedFormIds,
          userPermissions
        )
      )
    );
    this.tags$ = this.store.select(selectTags).pipe(map(tags => (tags ? tags.results : [])));
    this.categories$ = this.tags$.pipe(
      map(tagsData => tagsData.filter(data => data.type === CategoriesEnum.categories))
    );
    this.categoriesData$ = combineLatest(
      this.categories$,
      this.store.select(selectCategories),
      (t, c) => {
        if (c.length === 0 && t.length) {
          const categoriesListData = t.map(data => ({
            id: data.id, title: data.tag, color: TagHelpers.CategoriesRandomColor()
          }));
          this.store.dispatch(new ListCategoriesSuccess(categoriesListData));
          return categoriesListData;
        } else {
          return c;
        }
      }
    );
    this.statusData$ = this.posts$.pipe(
      map(posts => {
        const statusArray = [];
        posts.map(data => {
          if (!statusArray.some(m => m.title === PostHelpers.GetStatusLabelName(data.status))) {
            statusArray.push({
              title: PostHelpers.GetStatusLabelName(data.status), color: PostHelpers.GetStatusLabelColor(data.status)
            });
          }
        });
        return statusArray;
      })
    );
    this.markers$ = this.store.select(selectMap).pipe(
      map(mapData => (typeof mapData.markers !== 'undefined' ? mapData.markers : []))
    );
    this.aboutUsImageUrl$ = this.tenant$.pipe(
      map(tenant => {
        const image = tenant?.about_us?.about_us?.image;
        return image && tenant?.unique_name
          ? `assets/${tenant.unique_name}/${image}`
          : '';
      })
    );
  }
  filterActive: boolean = false;
  showFilter: number = null;
  preShowFilter: number = null;
  isClear: boolean = false;
  tagId: number = 0;
  openModal = new BehaviorSubject(false);
  formId: number = environment.form.id;
  requestOptions: ListPostOptions = {
    status: PostStatus.All,
    has_location: 'mapped',
    parent: 'none',
    form: 1
  };
  formList: Form[] = [];
  formIds: any = [];
  /** Formulario activo en el mapa (un solo id); el GeoJSON debe pedirse con esto, no con [1,2]. */
  selectedMapFormId = 1;
  /** Tras aplicar el formato predeterminado (`is_default`) o el primero permitido. */
  private mapPreferredFormApplied = false;
  /** Ignora respuestas GeoJSON obsoletas si el usuario cambió de formato. */
  private markersLoadGeneration = 0;
  PostType = PostType;
  type: string = PostType.Incident;
  source = interval(environment.mapRefreshInMinutes * 60 * 1000);
  mapCheck: boolean = true;
  mapMarkersInfo: any = [];

  lern_type: string;
  showSwitchMaps: boolean = false;
  showIReport: boolean = false;
  showLern: boolean = false;
  showMapView: boolean = false;
  showListView: boolean = false;
  showMap: boolean = true;
  description: string = '';

  /**
   * Selector Categorías / Estados en la leyenda: la opción marcada por defecto debe coincidir
   * con si el mapa colorea por tags (multiple_tags) o por estado del post.
   */
  get legendCategoriesList(): { label: string; value: string; checked: boolean }[] {
    const mt = this.requestOptions?.multiple_tags;
    let mapUsesCategoryColors = false;
    if (mt != null) {
      mapUsesCategoryColors = Array.isArray(mt) ? mt.length > 0 : true;
    }
    return [
      { label: 'Categories', value: 'categories', checked: mapUsesCategoryColors },
      { label: 'Status', value: 'status', checked: !mapUsesCategoryColors }
    ];
  }

  handleGlobalScroll = (e: WheelEvent) => {
    const sidebar = document.querySelector('.post-detail-container');
    const modal = document.querySelector('.modal-content');
    
    // Si el modal está abierto y el mouse está sobre él, permitir scroll
    if (modal && modal.contains(e.target as Node)) {
      e.stopPropagation();
      return;
    }
    
    // Si el sidebar no está visible, no hacer nada
    if (!this.showPostDetail || !sidebar) {
      return;
    }
    
    // Verificar si el mouse está sobre el sidebar
    const isOverSidebar = sidebar.contains(e.target as Node);
    
    if (isOverSidebar) {
      // Permitir scroll normal dentro del sidebar
      e.stopPropagation();
    } else {
      // Prevenir scroll si el mouse NO está sobre el sidebar
      e.preventDefault();
    }
  }

  updateSidebarPosition = () => {
    const navbar = document.querySelector('.navbar');
    const sidebar = document.querySelector('.post-detail-container');
    const overlay = document.querySelector('.sidebar-overlay');
    
    if (navbar && sidebar && overlay) {
      const navbarRect = navbar.getBoundingClientRect();
      const isNavbarVisible = navbarRect.bottom > 0;
      
      if (isNavbarVisible) {
        const topValue = Math.max(0, navbarRect.bottom);
        (sidebar as HTMLElement).style.top = `${topValue}px`;
        (sidebar as HTMLElement).style.height = `calc(100vh - ${topValue}px)`;
        (overlay as HTMLElement).style.top = `${topValue}px`;
        (overlay as HTMLElement).style.height = `calc(100vh - ${topValue}px)`;
      } else {
        (sidebar as HTMLElement).style.top = '0px';
        (sidebar as HTMLElement).style.height = '100vh';
        (overlay as HTMLElement).style.top = '0px';
        (overlay as HTMLElement).style.height = '100vh';
      }
    }
  }

  markers$: Observable<any[]>;





  ngOnInit() {
    this.catalog.ensureTags();
    window.addEventListener('wheel', this.handleGlobalScroll, { passive: false });
    window.addEventListener('scroll', this.updateSidebarPosition, { passive: true });
    window.addEventListener('resize', this.updateSidebarPosition, { passive: true });
    this.subs.add(
      this.store.select(selectCategories).pipe(
        map(data => ({
          data,
          signature: (data || [])
            .map(tag => tag.id)
            .sort((a, b) => a - b)
            .join(',')
        })),
        distinctUntilChanged((prev, curr) => prev.signature === curr.signature),
        map(result => result.data)
      ).subscribe(data => {
        this.showMap = true;
        const tagsArray: number[] = [];
        data.forEach(tag => tagsArray.push(tag.id));
        if (!this.requestOptions) {
          this.requestOptions = {
            status: PostStatus.All,
            has_location: 'mapped',
            parent: 'none',
            form: this.selectedMapFormId
          };
        }
        if (data.length) {
          this.requestOptions['multiple_tags'] = tagsArray;
        } else {
          delete this.requestOptions['multiple_tags'];
        }
        if (this.router.url.includes('mace')) {
          delete this.requestOptions['multiple_tags'];
        }
        this.requestOptions.form = this.selectedMapFormId;
        if (this.mapPreferredFormApplied) {
          this.reloadMapData();
        }
      })
    );

    // this.router.events.pipe(
    //   filter((event: RouterEvent) => event instanceof NavigationEnd)
    // ).subscribe((data) => {
    // });




    this.lern_type = this.route.snapshot.paramMap.get('type');

    this.navData$.subscribe(
      data => {
        const updatedData = data.map((v) => ({
          ...v,
          isActive: v.route === this.router.url
        }));

        // Perform any other actions with the updatedData if necessary
        // For example, set this.description based on the active route:
        const activeItem = updatedData.find(v => v.isActive);
        if (activeItem) {
          this.description = activeItem.description;
        }

        // If you need to update a state or service with the new data:
        // this.updateNavData(updatedData); // Assuming a method to handle the new data
      }
    );

    this.store.dispatch(new ListForms());

    this.subs.add(
      combineLatest([
        this.store.select(selectForms).pipe(filter(f => f !== null)),
        this.store.select(selectUser),
        this.formsWithPublishedPosts$,
        this.store.select(selectUserPermissions)
      ]).subscribe(([formsState, user, publishedFormIds, userPermissions]) => {
        this.publishedFormFilterIds =
          publishedFormIds === undefined ? null : publishedFormIds;
        if (!formsState?.results?.length) {
          return;
        }
        const probeReady =
          user == null
            ? publishedFormIds !== undefined
            : userPermissions != null;
        if (!probeReady) {
          return;
        }
        const allowed = MapPageComponent.getAllowedMapFormsSorted(
          formsState,
          user,
          publishedFormIds,
          userPermissions
        );
        this.formList = allowed;
        this.formIds = allowed.map(f => f.id);
        const allowedIds = this.formIds;
        if (!allowedIds.length) {
          // Logged-in without view grants only. Guests always get mappable forms above.
          this.selectedMapFormId = 0;
          this.formId = 0;
          if (this.requestOptions) {
            this.requestOptions.form = 0;
          }
          this.store.dispatch(new SetMapMarkers({ type: 'FeatureCollection', features: [] } as any));
          this.mapPreferredFormApplied = true;
          return;
        }
        if (!this.mapPreferredFormApplied) {
          const preferred = MapPageComponent.resolvePreferredMapFormId(
            formsState,
            user,
            publishedFormIds,
            this.router.url,
            userPermissions
          );
          this.selectMapForm(preferred, true);
          this.mapPreferredFormApplied = true;
          return;
        }
        if (
          allowedIds.length &&
          !allowedIds.includes(this.selectedMapFormId)
        ) {
          this.selectMapForm(
            MapPageComponent.resolvePreferredMapFormId(
              formsState,
              user,
              publishedFormIds,
              this.router.url,
              userPermissions
            ),
            true
          );
        }
      })
    );

    this.subs.add(
      combineLatest([
        this.store.select(selectUser),
        AuthHelpers.User.CanForm(this.store, null, 'view')
      ]).subscribe(([user, can]) => {
        // Guests need no grants — public map shows published reports only (API).
        // Only clear markers for authenticated users without any view grant.
        if (user != null && !can) {
          this.store.dispatch(
            new SetMapMarkers({ type: 'FeatureCollection', features: [] } as any)
          );
        }
      })
    );

    this.subs.add(
      combineLatest([
        this.store.select(selectForms).pipe(filter(forms => forms !== null)),
        this.store.select(selectUserPermissions),
        this.store.select(selectUser)
      ]).subscribe(([forms, userPermissions, user]) => {
        if (!forms) {
          return;
        }
        const allowed = MapPageComponent.getAllowedMapFormsSorted(
          forms,
          user,
          this.publishedFormFilterIds === null
            ? undefined
            : this.publishedFormFilterIds,
          userPermissions
        );
        this.formList = allowed;
        this.formId = this.selectedMapFormId;
        this.formIds = allowed.map(f => f.id);
        if (this.requestOptions && this.selectedMapFormId > 0) {
          this.requestOptions.form = this.selectedMapFormId;
        }
        if (this.selectedMapFormId > 0) {
          this.store.dispatch(new ListFormAttributes({ id: this.formId }));
        }
      })
    );

    this.subs.add(
      this.actions$
        .pipe(ofType<DeletedPost>(ECustomActions.DeletedPost))
        .subscribe(action => {
          this.requestOptions.offset = 0;
          this.requestOptions.limit = PAGE_SIZE;
          this.onPostDeleted();
        })
    );

    this.subs.add(this.source.subscribe(val => this.onRefreshClick()));

    document.body.style.alignItems = 'center';

    this.subs.add(
      AuthHelpers.User.HasUserPermission(
        this.store,
        Permission.CreateReport
      ).subscribe(can => {
        this.userCanSubmitPosts = can;
      })
    );

    // Show filters for everyone
    this.userCanFilterPosts = true;

    this.subs.add(
      this.router.events.subscribe(() => {
        document.body.style.alignItems = 'unset';
      })
    );

    this.subs.add(
      this.actions$
        .pipe(ofType<UserClickedMap>(EMapActions.UserClickedMap))
        .subscribe(action => {
          if (action.payload.actual && action.payload.actual.lat == null) {
            this.onRefreshClick();
          }
        })
    );

    this.subs.add(
      this.actions$
        .pipe(
          ofType<UserClickedMarker>(EMapActions.UserClickedMarker),
          switchMap(action => this.postService.read(action.payload.parent.id))
        )
        .subscribe(post => {
          this.showPostDetail = true;
          document.body.style.overflow = 'hidden';
          document.body.classList.add('sidebar-open');
          this.type = post.type;
          this.store.dispatch(new ListFormAttributes({ id: post.form.id }));
          this.store.dispatch(new ShowPostDetail(post));
          setTimeout(() => this.updateSidebarPosition(), 100);
        })
    );

    this.mapMarkersInfo = [
      { title: 'Homicide', color: '#E05F5F' },
      { title: 'Homicide', color: '#EDD138' },
      { title: 'Flood', color: '#F4AF4D' }
    ];
  }

  ngOnDestroy() {
    delete this.requestOptions['multiple_tags']
    this.mapCheck = false;
    this.formList = null;
    this.showMap = false;
    this.filterService.clearAll();
    this.subs.unsubscribe();
    window.removeEventListener('wheel', this.handleGlobalScroll);
    window.removeEventListener('scroll', this.updateSidebarPosition);
    window.removeEventListener('resize', this.updateSidebarPosition);
  }

  onNavClick(path) {
    this.router.navigate([path])
  }

  onClickViewDetail(post) {
    this.router.navigate(['/dashboard/posts/mace'], { state: { showPost: true, postData: post } })
  }

  onViewClick(tab: { route?: string; formId?: number }) {
    if (tab?.formId != null) {
      const fid = Number(tab.formId);
      if (fid > 0) {
        this.selectMapForm(fid, true);
        return;
      }
    }
    if (tab?.route) {
      this.router.navigate([tab.route]);
    }
  }

  private toFormNum(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
    return Number.isFinite(n) ? n : null;
  }

  /** Solo markers del formato activo (`formId` en GeoJSON o reglas legacy 1/2). */
  private featureMatchesSelectedForm(featureData: {
    properties?: { type?: string; formId?: number | string };
  }): boolean {
    const selectedId = this.toFormNum(this.requestOptions?.form);
    if (selectedId == null) {
      return true;
    }
    const featureFormId = this.toFormNum(featureData.properties?.formId);
    if (featureFormId != null) {
      return featureFormId === selectedId;
    }
    const t = featureData.properties?.type;
    if (selectedId === 1) {
      return t === PostType.Risk || t === 'risk';
    }
    if (selectedId === 2) {
      return t === PostType.Incident || t === 'incident';
    }
    return (
      t === 'report' ||
      t === PostType.Risk ||
      t === PostType.Incident ||
      t === 'risk' ||
      t === 'incident'
    );
  }

  loadMarkers(queryParams?: string[]) {
    this.isClear = false;
    if (queryParams) {
      for (const option in queryParams) {
        this.requestOptions[option] = queryParams[option];
      }
    }
    if (queryParams) {
      this.filterActive = true;
      this.requestOptions['parent'] = 'none';
    }
    if ((this.filterActive && this.requestOptions.form == 6) || this.router.url.includes('mace')) {
      this.requestOptions['type'] = 'report'
    }
    else {
      this.requestOptions['type'] = 'report'
    }
    
    const loadGeneration = ++this.markersLoadGeneration;

    combineLatest([
      this.postService.geoJson(this.requestOptions),
      this.store.select(selectPosts),
      this.store.select(selectCategories)
    ]).subscribe(([featureCollection, posts, categories]) => {
      if (loadGeneration !== this.markersLoadGeneration) {
        return;
      }

      let features = featureCollection?.features ?? [];
      features = features.filter(f => this.featureMatchesSelectedForm(f));

      if (!features.length) {
        this.store.dispatch(
          new SetMapMarkers({
            markers: []
          })
        );
        return;
      }

      if (this.router.url.includes('mace')) {

      if (this.requestOptions.hasOwnProperty('multiple_tags')) {
        features.map(featureData => {
          if (featureData.properties.tags && featureData.properties.tags.length) {
            let tagID = featureData.properties.tags[0];
            let tagColorInCategories = categories.find(item => item.id === parseInt(tagID));
            if (typeof (tagColorInCategories) !== 'undefined') {
              featureData.properties = { ...featureData.properties, color: tagColorInCategories.color };
            }
          }

        });
        const markers = FeatureHelpers.ToMarkers({
          ...featureCollection,
          features
        });
        this.store.dispatch(
          new SetMapMarkers({
            markers: markers
          })
        );
      }
      else if (!this.requestOptions.hasOwnProperty('multiple_tags')) {
        features.map(featureData => {
          let postID = featureData.properties.id;
          let postDetailObj = posts?.results
            ? posts.results.find(postData => postData.id === postID)
            : null;
          if (postDetailObj) {
            const statusColor = PostHelpers.GetStatusLabelColor(postDetailObj.status);
            featureData.properties = { ...featureData.properties, color: statusColor };
          } else {
            featureData.properties = { ...featureData.properties, color: 'rgb(255, 164, 51)' };
          }
        });
        const markers = FeatureHelpers.ToMarkers({
          ...featureCollection,
          features
        });
        this.store.dispatch(
          new SetMapMarkers({
            markers: markers
          })
        );
      }

      }
      else if (!this.router.url.includes('mace')) {
        if (this.requestOptions.hasOwnProperty('multiple_tags')) {
          features.map(featureData => {
            if (featureData.properties.tags && featureData.properties.tags.length) {
              let tagID = featureData.properties.tags[0];
              let tagColorInCategories = categories.find(item => item.id === parseInt(tagID));
              if (typeof (tagColorInCategories) !== 'undefined') {
                featureData.properties = { ...featureData.properties, color: tagColorInCategories.color };
              }
            }
          });
          const markers = FeatureHelpers.ToMarkers({
            ...featureCollection,
            features
          });
          this.store.dispatch(
            new SetMapMarkers({
              markers: markers
            })
          );
        }
        else if (!this.requestOptions.hasOwnProperty('multiple_tags')) {
          features.map(featureData => {
            let postID = featureData.properties.id;
            let postDetailObj = posts?.results
              ? posts.results.find(postData => postData.id === postID)
              : null;
            if (postDetailObj) {
              const statusColor = PostHelpers.GetStatusLabelColor(postDetailObj.status);
              featureData.properties = { ...featureData.properties, color: statusColor };
            } else {
              // Color por defecto
              featureData.properties = { ...featureData.properties, color: 'rgb(255, 164, 51)' };
            }
          });
          const markers = FeatureHelpers.ToMarkers({
            ...featureCollection,
            features
          });
          this.store.dispatch(
            new SetMapMarkers({
              markers: markers
            })
          );
        }
      }
    });
  }

  onRefreshClick(filterFlag?: boolean) {
    if (filterFlag) {
      this.filterActive = false;
      this.showFilter = null;
      if (this.selectedMapFormId) {
        this.filterService.clearForForm(this.selectedMapFormId);
      }
      this.requestOptions = {
        status: PostStatus.All,
        has_location: 'mapped',
        parent: 'none',
        form: this.selectedMapFormId
      };
    }
    if (filterFlag) {
      this.modalService.dismissAll();
    }
    this.loadMarkers();
  }

  /**
   * Leyenda: al cambiar modo se vuelve a pintar el mapa (por categoría de incidente vs por estado).
   */
  onLegendModeChange(value: string) {
    if (value === LegendTypeEnum.categories) {
      this.loadCategoriesData();
    } else {
      if (this.requestOptions) {
        delete this.requestOptions['multiple_tags'];
      }
      this.loadMarkers();
    }
  }

  loadCategoriesData() {
    this.subs.add(
      this.store.select(selectCategories).pipe(take(1)).subscribe(data => {
        if (data.length) {
          const tagsArray: number[] = [];
          data.forEach(tag => tagsArray.push(tag.id));
          this.requestOptions = {
            ...this.requestOptions,
            status: PostStatus.All,
            has_location: 'mapped',
            parent: this.requestOptions?.parent ?? 'none',
            form: this.selectedMapFormId,
            multiple_tags: tagsArray
          };
          this.loadMarkers();
        }
      })
    );
  }

  onPostUpdate() {
    // Recargar los posts con el nuevo estado
    let payload;
    if (this.router.url.includes('mace')) {
      payload = {
        status: PostStatus.All,
        offset: 0,
        parent: null,
        form: this.selectedMapFormId,
        type: 'report'
      };
    } else {
      payload = {
        status: PostStatus.All,
        offset: 0,
        parent: null,
        form: this.selectedMapFormId,
        type: 'report'
      };
    }
    this.store.dispatch(new GetPostsData(payload));
    
    // Recargar marcadores con nuevos colores
    setTimeout(() => {
      this.loadMarkers();
    }, 500);
  }

  onPostDeleted() {
    this.onPostDetailClose();
    this.subs.add(
      this.postService
        .geoJson(this.requestOptions)
        .subscribe(featureCollection => {
          this.store.dispatch(
            new SetMapMarkers({
              markers: FeatureHelpers.ToMarkers(featureCollection)
            })
          );
        })
    );
  }

  get activeFilterFormName(): string {
    const form = this.formList?.find(f => f.id === this.selectedMapFormId);
    return form?.name ?? '';
  }

  onFiltersClick(content) {
    this.prepareFilterModal();
    this.modalService.open(content, { centered: true, size: 'lg' });
    if (this.tagId > 0) {
      setTimeout(() => {
        this.store.dispatch(new TagSelectorSetById(this.tagId));
      });
    }
  }

  /** Opens filters for the map's active informe only (no in-modal form switch). */
  private prepareFilterModal(): void {
    const formId = this.selectedMapFormId;
    if (!formId || formId <= 0) {
      this.showFilter = null;
      return;
    }
    this.showFilter = formId;
    this.isClear = !this.filterActive;
    this.subs.add(
      this.store
        .select(selectFormAttributesLoadedFormId)
        .pipe(take(1))
        .subscribe(loadedFormId => {
          if (loadedFormId !== formId) {
            this.store.dispatch(new ListFormAttributes({ id: formId }));
          }
        })
    );
  }

  onInfoClick(content) {
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  onPostDetailClose() {
    this.showPostDetail = false;
    document.body.style.overflow = 'auto';
    document.body.classList.remove('sidebar-open');
    this.store.dispatch(new ShowPostDetail(null));
    // Restaurar el marcador clickeado a su estado original
    if (this.mapComponent) {
      this.mapComponent.unclickMapMarker();
    }
  }

  onShowChildPosts(parentId: number) {
    if (parentId) {
      this.requestOptions['parent'] = parentId.toString();
      this.showPostDetail = false;
      this.loadMarkers();
    }
  }

  onLinkPostClicked(data) {
    this.selectedPost = data.post;
    this.openModal.next(data.flag);
    if (!data.flag) {
      this.onApplyClick(data.flag);
    }
  }

  onCancelClick() {
    this.openModal.next(false);
  }

  onCloseClick() {
    this.modalService.dismissAll();
  }

  private initializeRequestOptions() {
    this.store.dispatch(new ListFormAttributes({ id: this.selectedMapFormId }));
    this.requestOptions = {
      status: PostStatus.All,
      has_location: 'mapped',
      parent: 'none',
      form: this.selectedMapFormId
    };
  }

  onFiltersApplyClick(queryParams) {
    this.initializeRequestOptions();
    delete this.requestOptions['multiple_tags'];
    this.loadMarkers(queryParams)
  }

  onApplyClick(dataObj) {
    if (!this.selectedPost?.id) {
      return;
    }
    // Patch link only — never resend form values from the detail view.
    const payload: Post = {
      id: this.selectedPost.id,
      parent_id: null
    } as Post;

    if (dataObj.flag) {
      const { status, id, mgmt_lev, priority } = dataObj.post;
      payload.parent_id = id;
      payload.status = status;
      payload.priority = priority;
      payload.mgmt_lev = mgmt_lev;
      this.selectedPost.parent_id = id;
    } else {
      this.selectedPost.parent_id = null;
    }

    this.subs.add(
      this.postService.update(payload).subscribe(post => {
        this.store.dispatch({
          type: ECustomActions.UpdatedPost,
          payload: post
        });
        this.store.dispatch({
          type: ECustomActions.ShowPostDetail,
          payload: post
        });
      })
    );
    this.modalService.dismissAll();
  }

  onClickRedirect(route: string) {
    this.router.navigateByUrl('dashboard/' + route);
  }

  private selectMapForm(formId: number, reloadData = false): void {
    if (!formId || formId <= 0) {
      this.selectedMapFormId = 0;
      this.formId = 0;
      this.showFilter = 0;
      if (this.requestOptions) {
        this.requestOptions.form = 0;
      }
      this.store.dispatch(
        new SetMapMarkers({ type: 'FeatureCollection', features: [] } as any)
      );
      return;
    }
    this.selectedMapFormId = formId;
    this.formId = formId;
    this.showFilter = formId;
    if (this.requestOptions) {
      this.requestOptions.form = formId;
    }
    this.store.dispatch(new ListFormAttributes({ id: formId }));
    if (reloadData) {
      this.reloadMapData();
    }
  }

  private reloadMapData(): void {
    this.store.dispatch(
      new GetPostsData({
        status: PostStatus.All,
        offset: 0,
        parent: null,
        form: this.selectedMapFormId,
        type: 'report'
      })
    );
    setTimeout(() => this.loadMarkers(), 100);
  }

  /** Primero `is_default` entre formatos permitidos (`sortFormsDefaultFirst`). */
  private static resolvePreferredMapFormId(
    formsState: { results?: Form[] } | null,
    user: User | null | undefined,
    publishedFormIds: number[] | undefined,
    url: string,
    userPermissions?: UserPermissions | null
  ): number {
    const allowed = MapPageComponent.getAllowedMapFormsSorted(
      formsState,
      user,
      publishedFormIds,
      userPermissions
    );
    if (allowed.length) {
      return allowed[0].id;
    }
    // Logged-in users without view grants: do not fall back to form 1/2
    if (user != null) {
      return 0;
    }
    const mappable = MapPageComponent.mappableFormIds(formsState);
    if (mappable.length) {
      return mappable[0];
    }
    return url.includes('mace') ? 2 : 1;
  }

  /** Anónimo: formatos con al menos un informe público (sin exigir ubicación). */
  private fetchFormIdsWithPublishedPosts(
    formsState: { results?: Form[] } | null
  ): Observable<number[]> {
    const formIds = MapPageComponent.mappableFormIds(formsState);
    if (!formIds.length) {
      return of([]);
    }
    return forkJoin(
      formIds.map(fid =>
        this.postService
          .list({
            status: PostStatus.All,
            published: true,
            parent: 'none',
            form: fid,
            type: 'report',
            limit: 1,
            offset: 0
          })
          .pipe(
            map(page =>
              (page?.total_count ?? page?.results?.length ?? 0) > 0 ? fid : null
            ),
            catchError(() => of(null))
          )
      )
    ).pipe(
      map((flags: (number | null)[]) =>
        flags.filter((x): x is number => x != null)
      )
    );
  }

  private static mappableFormIds(
    formsState: { results?: Form[] } | null
  ): number[] {
    const results = formsState?.results ?? [];
    return sortFormsDefaultFirst(
      results.filter(
        f => !f.disabled && MapPageComponent.mapSwitcherMappableFormType(f)
      )
    ).map(f => f.id);
  }

  private static mapSwitcherMappableFormType(form: Form): boolean {
    const t = form.type;
    if (t == null || String(t).trim() === '') {
      return true;
    }
    const s = String(t);
    return (
      s === PostType.Incident ||
      s === PostType.Risk ||
      s === 'incident' ||
      s === 'risk' ||
      s === 'report'
    );
  }

  /**
   * Rail del mapa:
   * - logueado → solo formatos con grant `view` (empty = none)
   * - anónimo → sin grants; todos los formatos mapeables (API filtra `posts.published`).
   *   publishedFormIds solo acota el rail cuando la sonda encontró formatos con data.
   */
  private static getAllowedMapFormsSorted(
    formsState: { results?: Form[] } | null,
    user: User | null | undefined,
    publishedFormIds?: number[],
    userPermissions?: UserPermissions | null
  ): Form[] {
    const results = formsState?.results ?? [];
    if (!results.length) {
      return [];
    }
    let base = sortFormsDefaultFirst(
      results.filter(
        f =>
          !f.disabled && MapPageComponent.mapSwitcherMappableFormType(f)
      )
    );
    if (user != null) {
      if (!userPermissions) {
        return [];
      }
      if (userPermissions.isAdmin) {
        return base;
      }
      const viewIds = AuthHelpers.User.accessibleFormIdsSync(
        userPermissions,
        'view'
      );
      if (viewIds === null) {
        return base;
      }
      if (!viewIds.length) {
        return [];
      }
      return base.filter(f => viewIds.includes(f.id));
    }
    // Guest: no form grants. Optional probe only narrows when it found public data.
    if (publishedFormIds && publishedFormIds.length > 0) {
      base = base.filter(f => publishedFormIds.includes(f.id));
    }
    return base;
  }

  private static buildMapViewDataTabs(
    tenant: any,
    formsState: { results?: Form[] } | null,
    user: User | null | undefined,
    currentUrl: string,
    publishedFormIds?: number[],
    userPermissions?: UserPermissions | null
  ): any[] {
    const rawView: any[] = tenant?.mapNavigation?.viewData;
    const results = formsState?.results ?? [];
    if (!results.length) {
      return Array.isArray(rawView) ? [...rawView] : [];
    }
    const allowedForms = MapPageComponent.getAllowedMapFormsSorted(
      formsState,
      user,
      publishedFormIds,
      userPermissions
    );
    const listTab =
      Array.isArray(rawView) &&
      rawView.find(
        (v: { route?: string }) =>
          v?.route && String(v.route).indexOf('/posts') >= 0
      );
    const mapRouteEntry =
      Array.isArray(rawView) &&
      rawView.find(
        (v: { route?: string }) =>
          v?.route &&
          String(v.route).indexOf('/map') >= 0 &&
          String(v.route).indexOf('/posts') < 0
      );
    const fallbackRoute = currentUrl.includes('mace')
      ? '/dashboard/map/mace'
      : '/dashboard/map';
    const mapTabRoute =
      mapRouteEntry && mapRouteEntry.route
        ? mapRouteEntry.route
        : fallbackRoute;
    const formTabs = allowedForms.map(form => ({
      label: form.name,
      displayLabel: form.name || '',
      formId: form.id,
      route: mapTabRoute,
      activeForRoutes: ['/dashboard/map', '/dashboard/map/mace'],
      isActive: false
    }));
    const canSeeList = allowedForms.length > 0;
    const includeListTab = !!listTab && canSeeList;
    return includeListTab
      ? [
          ...formTabs,
          {
            ...listTab,
            isActive: false,
            viewTabRole: 'dataListLink'
          }
        ]
      : formTabs;
  }
}
